<template lang="pug">
  .welcome

    .message.is-primary(v-if="showWelcomeMessage")
      .message-header
        p Welcome to OCTea!
        button.delete(aria-label="delete", @click="closeWelcomeMessage")
      .message-body
        p
          strong OCTea is an app that makes it easy to brew good tea. &nbsp;
          | Simply select what tea, brewing style, and the size of your cup and OCTea will give you suggestions for quantity, temperature and time, guiding you through the process.
        p
          strong Note:&nbsp;
          | If you're not used to the metric system, you might want to have a look at the&nbsp;
          router-link(:to="{name: 'Settings'}") Settings Menu
          | .

    .columns
      .column.is-one-third
        h2.subtitle 1. Get Ready!

        .field
          label.label I want to brew...
          .control.select.is-primary.is-fullwidth
            select(v-model="tea")
              option(value="manual") Something else (manual mode)
              option(v-for="t in teas", :value="t.id") {{t.name}} ({{t.temperature | temperature}})
                span(v-if="t.needs_washing") *
          .right.small * needs washing

        .group(v-if="!manual")

          .field
            label.label Style
            .control
              template(v-for="s in availableStyles")
                label(:for="'style-' + s.id").radio
                  input(type="radio", name="style", v-model="brewStyle", :value="s.id", :id="'style-' + s.id")
                  | &nbsp;{{s.name}}&nbsp;
                  span.small ({{teaObject.styles[s.id].n_infusions}} infusions)
                a(v-if="s.url", :href="s.url", target="_blank").style-detail ?
          .field
            label.label Quantity: {{cup | volume}}
            .control
              input(type='range', step="10", min="10", max="500", v-model="cup")#quantity.slider.is-fullwidth

        .group(v-else)
          label.label Duration (s)

          .field.has-addons
            p.control
              input.input(v-model="manualInitial" :class="{'is-danger': !isNumeric(manualInitial)}" type="number" step="5" min="0" required)
            p.control
              span(style="padding: 10px 5px 0 5px; font-weight: bold;") +
            p.control
              input.input(v-model="manualIncrement" :class="{'is-danger': !isNumeric(manualIncrement)}" type="number" step="5" min="0" required)

      .column
        h2.subtitle 2. Set!

        .group(v-if="!manual")
          .message.is-info
            .message-header
              p Prepare to brew
            .message-body

              ol(style="margin-left: 20px")
                li.quantity
                  | For {{cup | volume}} of {{teaObject.name}} tea brewed {{styleObject.name}} style, you will need&nbsp;
                  strong {{quantity | weight}}&nbsp;
                  | of tea.
                li.temperature
                  | Pre-heat the kettle to&nbsp;
                  strong {{teaObject.temperature | temperature}}
                  | .
                li.wash(v-if="teaObject.needs_washing") Using the hot water, rinse the tea for 5 seconds to open up the leaves, then pour the wash. You can also use the wash to pre-heat the cups and pot.
                li.heat(v-else) Use the hot water to pre-heat the cups and pot.
        .group(v-else)
          .message.is-info
            .message-header
              p Prepare to brew
            .message-body
              p If you choose manual mode, you probably know what you want to do :)

    h2.subtitle 3. Brew!
    a(@click="brewClicked").button.is-primary.is-large
      span.icon
        fa-icon(icon='thermometer-three-quarters')
      span Brew!
    .small We will remember your settings for next time.

</template>

<script>
import teaData from '../data/teas.json'
import styleData from '../data/styles.json'

import router from '../router'

import { getCurrentVersion } from '../version'

function roundToTenth (x) {
  return (0.1 * Math.round(x * 10)).toFixed(1)
}

export default {
  name: 'Welcome',
  props: {
  },
  data () {
    return {
      teas: teaData,
      styles: styleData,
      tea: this.$localStorage.get('tea', 'green'),
      brewStyle: this.$localStorage.get('brewStyle', 'gongfu'),
      cup: this.$localStorage.get('cup', 300),
      showWelcomeMessage: this.$localStorage.get('showWelcomeMessage', true) !== 'false',
      manualInitial: this.$localStorage.get('manualInitial', 120),
      manualIncrement: this.$localStorage.get('manualIncrement', 30)
    }
  },
  watch: {
    tea () {
      if (this.tea === 'manual') { return }
      let teaStyles = this.teaObject.styles
      if (!teaStyles.hasOwnProperty(this.brewStyle)) {
        if (teaStyles.hasOwnProperty('gongfu')) {
          this.brewStyle = 'gongfu'
        } else if (teaStyles.hasOwnProperty('japanese')) {
          this.brewStyle = 'japanese'
        }
      }
    }
  },
  computed: {
    manual () {
      return this.tea === 'manual'
    },
    teaObject () {
      return teaData[this.tea] || {}
    },
    styleObject () {
      return styleData[this.brewStyle]
    },
    teaStyleObject () {
      return teaData[this.tea].styles[this.brewStyle]
    },
    quantity () {
      let tso = this.teaStyleObject
      if (!tso) { return '0' }
      let amountPer100 = tso.amount

      if (Array.isArray(amountPer100)) {
        let minAmount = roundToTenth(amountPer100[0] * (this.cup / 100))
        let maxAmount = roundToTenth(amountPer100[1] * (this.cup / 100))
        return [minAmount, maxAmount]
      } else {
        return roundToTenth(amountPer100 * (this.cup / 100))
      }
    },
    availableStyles () {
      return Object.values(styleData).filter((s) => this.teaObject.styles.hasOwnProperty(s.id))
    }
  },
  mounted () {
    let currentVersion = getCurrentVersion()
    let lastSeenVersion = this.$localStorage.get('lastSeenVersion', currentVersion)
    this.$localStorage.set('lastSeenVersion', currentVersion)

    if (lastSeenVersion !== currentVersion) {
      this.$router.push({ 'name': 'ReleaseNotes' })
    }
  },
  methods: {
    isNumeric (s) {
      return (!isNaN(s)) && (s !== '')
    },
    brewClicked () {
      this.$localStorage.set('tea', this.tea)
      this.$localStorage.set('brewStyle', this.brewStyle)
      this.$localStorage.set('cup', this.cup)
      this.$localStorage.set('manualInitial', this.manualInitial)
      this.$localStorage.set('manualIncrement', this.manualIncrement)

      if (this.manual) {
        if (!this.isNumeric(this.manualInitial)) {
          alert('Your initial brewing time is not a number!')
          return
        }

        if (!this.isNumeric(this.manualIncrement)) {
          alert('Your brewing increment is not a number!')
          return
        }

        if (+this.manualInitial < 0 || +this.manualIncrement < 0) {
          alert('Cannot enter negative brewing times!')
          return
        }

        router.push({
          name: 'BrewingManual',
          params: {
            manual: `${this.manualInitial}+${this.manualIncrement}`,
            infusion: 1
          }
        })
      } else {
        router.push({
          name: 'Brewing',
          params: {
            tea: this.tea,
            brewStyle: this.brewStyle,
            cup: this.cup,
            infusion: 1
          }
        })
      }
    },
    closeWelcomeMessage () {
      this.showWelcomeMessage = false
      this.$localStorage.set('showWelcomeMessage', false)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="stylus">
.small
  padding 0
  font-size 70%

.right
  text-align right

.style-detail
  vertical-align super
  padding-right 10px
</style>
